import './index.less';

import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useToggle } from 'ahooks';
import { Button, message, Table } from 'antd';
import classNames from 'classnames';
import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUserInfo } from '@/hooks/useUserInfo';
import { useI18nContext } from '@/i18n/I18n';
import { getAuditItemInfo, getPorInfo } from '@/services/por';
import { isMobile } from '@/utils/ua';

import { ByList } from '../security/comps/ByList';
import AuditCard from './comps/auditCard';
import ReserveDetailModal from './comps/reserveDetailModal';
import { IReserveInfo } from './types';

const Audit: FC = () => {
  const [$t, lang] = useI18nContext();
  const { msgError, user } = useUserInfo();
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState<
    {
      auditId: string;
      auditDate: string;
      auditType: string;
      walletAssetFile: string;
    }[]
  >([]);
  const [currentReserve, setCurrentReserve] = useState<IReserveInfo>();
  const [detailVisible, { toggle }] = useToggle<boolean>(false);
  const [downFileUrl, setDownFileUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 请求审计列表数据
    getPorInfo()
      .then((res) => {
        const items = res?.result?.items || [];
        setDataSource(
          items?.map((item, index) => ({
            auditId: item.auditId,
            auditDate: item.snapshotDt.replace(
              /^(\d{4})(\d{2})(\d{2})(\d{2})$/,
              '$1-$2-$3 $4:00 UTC',
            ),
            auditType: item.auditType || '1',
            walletAssetFile: item.walletAssetFile,
          })),
        );
      })
      .catch((err: any) => {
        if (err) {
          msgError(err);
        } else {
          message.error('network error! try again later');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (user.is_submember) {
      // 母账号时在 /app/user/audit页面切换子账号 禁止子账号看到audit页面
      navigate('/app/user/security');
    }
  }, [user]);

  // 打开/关闭准备金详情弹框
  const toggleReserveModal = (auditId?: string, date?: string) => {
    if (auditId) {
      getAuditItemInfo({ auditId })
        .then((res: any) => {
          setCurrentReserve({
            ...(res?.result || {}),
            auditId: res?.result?.auditId || auditId,
            date: res?.result?.date || date,
          });
          toggle();
        })
        .catch((err: any) => {
          if (err) {
            msgError(err);
          } else {
            message.error('network error! try again later');
          }
        });

      const downFileUrl = dataSource.find(
        (item) => item.auditId === auditId,
      )?.walletAssetFile;
      setDownFileUrl(downFileUrl || '');
    } else {
      toggle();
    }
  };

  const columns: any[] = [
    {
      title: $t('auditNumber'),
      width: 294,
      dataIndex: 'auditId',
    },
    {
      title: $t('Date'),
      width: 294,
      dataIndex: 'auditDate',
    },
    {
      title: $t('auditType'),
      width: 294,
      dataIndex: 'auditType',
      render: (auditType: string | number, record: any) => {
        let type: any = '';
        if (auditType == '1') {
          type = $t('reserveProof');
        } else {
          type = auditType;
        }
        return <span className="td-auditType">{type}</span>;
      },
    },
    {
      title: $t('child.operation'),
      fixed: 'right',
      width: 160,
      render: (_: any, record: any, index: number) => (
        <a onClick={() => toggleReserveModal(record.auditId, record.auditDate)}>
          {$t('child.details')}
        </a>
      ),
    },
  ];

  const goOtherPage = (e: any) => {
    e && e.preventDefault();
    navigate('/app/user/proof-of-reserve');
  };
  const returnReserve = (e: any) => {
    e && e.preventDefault();
    navigate('/app/user/proof-of-reserve');
  };
  if (isMobile()) {
    return (
      <>
        <div
          style={{
            minWidth: isMobile() ? '100vw' : 'auto',
          }}
        >
          <div className="audit-fix-title">
            <div className="returnIcon" onClick={returnReserve}>
              <Button
                type="link"
                className={classNames('back-btn', {
                  'right-position': lang === 'ar-SA',
                  'left-position': lang !== 'ar-SA',
                })}
                icon={
                  lang === 'ar-SA' ? (
                    <ArrowRightOutlined />
                  ) : (
                    <ArrowLeftOutlined />
                  )
                }
                onClick={returnReserve}
              ></Button>
            </div>
            <h1 className="audit-title">{$t('audit')}</h1>
          </div>
          <div className="audit-box">
            <h1 className="audit-title">{$t('audit')}</h1>
            {dataSource.map((item: any) => {
              return (
                <AuditCard
                  {...item}
                  key={item.auditId}
                  toggleReserveModal={() =>
                    toggleReserveModal(item.auditId, item.auditDate)
                  }
                />
              );
            })}
            <div className="reserveProofAudit">{$t('reserveProofAudit')}</div>
            <div className={classNames('link-container')}>
              <a
                className="proof-reserve-link"
                href={'/app/user/proof-of-reserve'}
                onClick={goOtherPage}
              >
                {$t('learnAboutReserveProof')}&nbsp;&nbsp;
                <ArrowRightOutlined />
              </a>
            </div>
          </div>
          {detailVisible && (
            <ReserveDetailModal
              visible={detailVisible}
              downFileUrl={downFileUrl}
              reserveInfo={currentReserve}
              onCancel={toggleReserveModal}
            />
          )}
        </div>
      </>
    );
  }
  return (
    <div className={classNames('audit-contianer')}>
      <ByList title={$t('audit')}>
        <div style={{ padding: '0px 32px' }}>
          <Table
            scroll={{ x: 1176 }}
            ref={anchorRef}
            className="bybit-table"
            columns={columns}
            loading={loading}
            dataSource={dataSource}
            pagination={{
              hideOnSinglePage: true,
              total: dataSource.length,
              pageSize: 10,
              onChange: () => {
                const top = anchorRef.current?.offsetTop;
                window.scrollTo({ top: top || 0, behavior: 'smooth' });
              },
            }}
          ></Table>

          <div className="reserveProofAudit">{$t('reserveProofAudit')}</div>
          <div className={classNames('link-container')}>
            <a
              className="proof-reserve-link"
              href={'/app/user/proof-of-reserve'}
              onClick={goOtherPage}
            >
              {$t('learnAboutReserveProof')}&nbsp;&nbsp;
              <ArrowRightOutlined />
            </a>
          </div>
        </div>
      </ByList>
      {detailVisible && (
        <ReserveDetailModal
          visible={detailVisible}
          downFileUrl={downFileUrl}
          reserveInfo={currentReserve}
          onCancel={toggleReserveModal}
        />
      )}
    </div>
  );
};

export default Audit;
